import React, { useEffect, useState } from "react";
import { getUnitName, unitLanguage } from "../../utils/unitNames";

const messages = {
  en: { loading: "Checking the unit combination…", failure: "Could not check the unit combination.", retry: "Retry",
    factor: "Enter a valid conversion quantity.", blocked: "This unit combination is blocked.",
    review: "This unit combination needs review.", note: "Justification", required: "Explain why this combination is correct before saving." },
  es: { loading: "Comprobando la combinación de unidades…", failure: "No se ha podido comprobar la combinación de unidades.", retry: "Reintentar",
    factor: "Indica una cantidad de conversión válida.", blocked: "Esta combinación de unidades está bloqueada.",
    review: "Esta combinación de unidades requiere revisión.", note: "Justificación", required: "Explica por qué esta combinación es correcta antes de guardar." },
  ca: { loading: "S’està comprovant la combinació d’unitats…", failure: "No s’ha pogut comprovar la combinació d’unitats.", retry: "Torna-ho a provar",
    factor: "Indica una quantitat de conversió vàlida.", blocked: "Aquesta combinació d’unitats està bloquejada.",
    review: "Aquesta combinació d’unitats requereix revisió.", note: "Justificació", required: "Explica per què aquesta combinació és correcta abans de desar." },
};

const configKey = (item) => JSON.stringify([String(item.unit_id || ""), String(item.subunit_id || ""),
  item.subunit_id ? Number(item.pack_size) : null, String(item.subunit2_id || ""), item.subunit2_id ? Number(item.subunit2_size) : null]);

export function useItemUnitCompatibility({ form, original, units, active, client, requestOptions, corporate, languageId }) {
  const language = unitLanguage(languageId);
  const copy = messages[language] || messages.en;
  const unchanged = Boolean(original && configKey(original) === configKey(form));
  const notes = form.unit_justifications || {};
  const check = active && form.unit_id && form.subunit_id && (!unchanged || Object.keys(original.unit_justifications || {}).length > 0);
  const code = (id) => units.find((unit) => String(unit.id) === String(id))?.code;
  const payload = { unit_code: code(form.unit_id), subunit_code: code(form.subunit_id),
    subunit2_code: form.subunit2_id ? code(form.subunit2_id) : null, pack_size: form.pack_size ? Number(form.pack_size) : null,
    subunit2_size: form.subunit2_id && form.subunit2_size ? Number(form.subunit2_size) : null, language };
  const validCodes = payload.unit_code && payload.subunit_code && (!form.subunit2_id || payload.subunit2_code);
  const validFactors = payload.pack_size > 0 && (!form.subunit2_id || payload.subunit2_size > 0);
  const body = check && validCodes ? JSON.stringify(payload) : null;
  const url = corporate ? "/api/admin/purchasing/units/compatibility" : "/api/purchasing/units/compatibility";
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState(null);
  useEffect(() => {
    if (!body) { setResult(null); return undefined; }
    const controller = new AbortController();
    const load = async () => {
      try {
        const response = await client.post(url, JSON.parse(body), { ...requestOptions?.(), signal: controller.signal });
        if (!Array.isArray(response?.data?.edges) || typeof response.data.blocked !== "boolean") throw new Error();
        if (!controller.signal.aborted) setResult({ body, url, attempt, data: response.data });
      } catch (error) {
        if (!controller.signal.aborted) setResult({ body, url, attempt, error: error.response?.data?.error || true });
      }
    };
    load();
    return () => controller.abort();
  }, [body, client, requestOptions, url, attempt]);
  const current = body && result?.body === body && result.url === url && result.attempt === attempt ? result : null;
  const assessed = current?.data?.edges || [];
  const blocks = assessed.filter((edge) => edge.status === "block");
  const reviews = assessed.filter((edge) => edge.status === "review");
  const edges = [...blocks, ...reviews];
  const justifications = Object.fromEntries(reviews.map(({ key }) => [key, (notes[key] || "").trim()]));
  const originalNotes = original?.unit_justifications || {};
  const noteKeys = current?.data ? reviews.map(({ key }) => key) : Object.keys(notes);
  const notesChanged = unchanged && check && noteKeys.some((key) => (notes[key] || "").trim() !== (originalNotes[key] || "").trim());
  const enforce = !unchanged || notesChanged;
  let message = "";
  if (check) {
    if (!validCodes || current?.error) message = typeof current?.error === "string" ? current.error : copy.failure;
    else if (!current?.data) message = copy.loading;
    else if (current.data.blocked) message = copy.blocked;
    else if (!validFactors) message = copy.factor;
    else if (Object.values(justifications).some((text) => !text || text.length > 1000)) message = copy.required;
  }
  const blockedReason = blocks.length ? [blocks[0].reason, blocks[0].condition].filter(Boolean).join(" ") : "";
  return { copy, edges, notes, units, language, message: blocks.length ? "" : message, retry: current?.error ? () => setAttempt((n) => n + 1) : null,
    problem: enforce ? blockedReason || message : "",
    fields: enforce && (reviews.length || Object.keys(original?.unit_justifications || {}).length) ? { unit_justifications: justifications } : {} };
}

export default function ItemUnitCompatibility({ policy, setForm }) {
  const { copy, edges, notes, units, language, message } = policy;
  if (!message && !edges.length) return null;
  const label = (code) => getUnitName(units.find((unit) => unit.code?.toLowerCase() === code?.toLowerCase()), language) || code;
  const heading = (edge) => edge.status !== "block" ? copy.review : edge.rule === "factor" ? copy.factor : copy.blocked;
  return <div className="item-unit-policy">
    {message && <p role="status">{message} {policy.retry && <button type="button" onClick={policy.retry}>{copy.retry}</button>}</p>}
    {edges.map((edge) => <div key={`${edge.level}-${edge.rule || edge.status}`} className={`item-unit-policy__edge item-unit-policy__edge--${edge.status}`}>
      <strong>{heading(edge)} {label(edge.parent_code)} → {label(edge.child_code)}</strong>
      <p>{edge.reason}</p>
      {edge.condition && edge.condition !== edge.reason && <p>{edge.condition}</p>}
      {edge.status === "review" && <label>{copy.note} · {label(edge.parent_code)} → {label(edge.child_code)}
        <textarea className="form-control spx-input" rows={2} maxLength={1000} value={notes[edge.key] || ""}
          aria-required="true" onChange={(event) => {
            const value = event.target.value;
            setForm((prev) => ({ ...prev, unit_justifications: { ...prev.unit_justifications, [edge.key]: value } }));
          }} />
      </label>}
    </div>)}
  </div>;
}
